import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getSocket } from '../../api/socket';
import { fetchGameConfig } from '../../api/gameConfig';

export interface ServerQueryResult {
  online: boolean;
  protocol: 'source-query' | 'none';
  ping_ms: number | null;
  server_name: string | null;
  map: string | null;
  player_count: number | null;
  max_players: number | null;
  queried_at: string;
}

interface Props {
  gameId: string;
  /** Current systemd active state — used to show "starting up" warning */
  systemdActive: string;
  provider?: string;
}

function pingColor(ms: number): 'success' | 'warning' | 'error' {
  if (ms < 50) return 'success';
  if (ms < 200) return 'warning';
  return 'error';
}

export function StatusTab({ gameId, systemdActive, provider }: Props) {
  const [result, setResult] = useState<ServerQueryResult | null>(null);
  const [inviteCode, setInviteCode] = useState<string | null>(null);

  useEffect(() => {
    if (!gameId) return;
    const socket = getSocket();

    const handler = (data: { game_id: string; result: ServerQueryResult }) => {
      if (data.game_id === gameId) setResult(data.result);
    };

    socket.emit('games:query:subscribe', { game_id: gameId });
    socket.on('games:query:result', handler);

    return () => {
      socket.emit('games:query:unsubscribe', { game_id: gameId });
      socket.off('games:query:result', handler);
    };
  }, [gameId]);

  useEffect(() => {
    if (provider !== 'windrose') return;
    fetchGameConfig(gameId)
      .then((r) => setInviteCode(String(r.values.InviteCode ?? '')))
      .catch(() => {});
  }, [gameId, provider]);

  if (result === null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        <CircularProgress size={12} />
        <Typography variant="caption" color="text.secondary">Abfrage läuft…</Typography>
      </Box>
    );
  }

  if (result.protocol === 'none') {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: provider === 'windrose' && inviteCode ? 'monospace' : undefined }}>
        {provider === 'windrose' ? ("Invite Code " + inviteCode || 'NAT punch-through – invite code') : 'Keine Query-Unterstützung'}
      </Typography>
    );
  }

  if (!result.online) {
    const isStarting = systemdActive === 'active' || systemdActive === 'activating';
    return (
      <Typography variant="caption" color={isStarting ? 'warning.main' : 'text.disabled'}>
        {isStarting ? 'Startet noch…' : 'Offline'}
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
      {result.ping_ms !== null && (
        <Chip
          label={`${result.ping_ms} ms`}
          color={pingColor(result.ping_ms)}
          size="small"
          sx={{ height: 18, fontSize: '0.65rem' }}
        />
      )}
      {result.player_count !== null && result.max_players !== null && (
        <Chip
          label={`${result.player_count}/${result.max_players}`}
          size="small"
          sx={{ height: 18, fontSize: '0.65rem' }}
        />
      )}
      {result.map && (
        <Typography variant="caption" color="text.secondary" noWrap>
          {result.map}
        </Typography>
      )}
    </Stack>
  );
}

// Larger variant for a dialog/drawer — same subscription logic, richer layout
export function StatusPanel({ gameId, systemdActive, provider }: Props) {
  const [result, setResult] = useState<ServerQueryResult | null>(null);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!gameId) return;
    const socket = getSocket();

    const handler = (data: { game_id: string; result: ServerQueryResult }) => {
      if (data.game_id === gameId) setResult(data.result);
    };

    socket.emit('games:query:subscribe', { game_id: gameId });
    socket.on('games:query:result', handler);

    return () => {
      socket.emit('games:query:unsubscribe', { game_id: gameId });
      socket.off('games:query:result', handler);
    };
  }, [gameId]);

  useEffect(() => {
    if (provider !== 'windrose') return;
    fetchGameConfig(gameId)
      .then((r) => setInviteCode(String(r.values.InviteCode ?? '')))
      .catch(() => {});
  }, [gameId, provider]);

  if (result === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (result.protocol === 'none') {
    return (
      <Stack spacing={2}>
        {provider === 'windrose' && (
          <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Invite code — share with players
              </Typography>
              <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: 2 }}>
                {inviteCode || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Windrose → Play → Connect to Server
              </Typography>
            </Box>
            <Tooltip title={copied ? 'Copied!' : 'Copy invite code'}>
              <IconButton
                onClick={() => {
                  void navigator.clipboard.writeText(inviteCode ?? '');
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Paper>
        )}
        <Alert severity="info">
          {provider === 'windrose'
            ? 'Windrose uses NAT punch-through — no port forwarding needed. Players connect via invite code, not IP address.'
            : 'Dieser Server unterstützt keine Live-Status-Abfrage.'}
        </Alert>
      </Stack>
    );
  }

  if (!result.online) {
    const isStarting = systemdActive === 'active' || systemdActive === 'activating';
    if (isStarting) {
      return (
        <Alert severity="warning">
          Server läuft, antwortet noch nicht — möglicherweise noch beim Starten.
        </Alert>
      );
    }
    return (
      <Alert severity="info">Server ist offline.</Alert>
    );
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        {result.ping_ms !== null && (
          <Chip
            label={`${result.ping_ms} ms`}
            color={pingColor(result.ping_ms)}
            size="small"
          />
        )}
        {result.player_count !== null && result.max_players !== null && (
          <Chip
            label={`${result.player_count} / ${result.max_players} Spieler`}
            size="small"
          />
        )}
      </Stack>
      {result.map && (
        <Typography variant="body2" color="text.secondary">
          Karte: {result.map}
        </Typography>
      )}
      {result.server_name && (
        <Typography variant="body2" color="text.secondary">
          Name: {result.server_name}
        </Typography>
      )}
    </Stack>
  );
}
